/**
 * Ensures node/edge ids are unique so Svelte keyed {#each} (and XYFlow) never throw.
 * Drops duplicate nodes (keeps first occurrence), fixes duplicate edge ids, removes dangling edges,
 * and repairs invalid XYFlow handles (avoids React Flow #008 dev warnings).
 */

import type { FlowEdge, FlowNode } from '../types/flow-types';
import { isDeviceCheckSingleOutput } from './deviceCheckMode';

/** Condition / branch nodes that expose true/false (or case-N / default) sources — not a single "output". */
const BRANCH_SOURCE_TYPES = new Set<string>([
	'condition',
	'timeCondition',
	'manualCondition',
	'weatherCondition',
	'sunriseSunsetCondition',
	'energyCondition',
	'sceneCondition',
	'alarmCondition',
	'scheduleCondition',
	'variableCondition',
	'logicGate',
	'hysteresisCondition',
	'presenceCondition',
	'virtualComponentCondition',
]);

function validSourceHandleIds(node: FlowNode): Set<string> {
	const t = String(node.type || '');
	if (t === 'switchNode' || t === 'variableSwitch') {
		const ids = new Set<string>(['default']);
		const cases = (node.data as Record<string, unknown> | undefined)?.cases;
		if (Array.isArray(cases)) {
			cases.forEach((_, i) => ids.add(`case-${i}`));
		}
		return ids;
	}
	if (t === 'condition' && isDeviceCheckSingleOutput(node)) {
		return new Set(['true']);
	}
	if (t === 'timeCondition') {
		return new Set(['true']);
	}
	if (t === 'virtualComponentEvent') return new Set(['true']);
	if (BRANCH_SOURCE_TYPES.has(t)) {
		return new Set(['true', 'false']);
	}
	return new Set(['output']);
}

function validTargetHandleIds(node: FlowNode): Set<string> {
	const t = String(node.type || '');
	if (t === 'logicGate') {
		const n = Math.max(2, Number((node.data as any)?.conditionCount) || 2);
		const ids = new Set<string>();
		for (let i = 0; i < n; i++) ids.add(`input-${i}`);
		return ids;
	}
	return new Set(['input']);
}

function pickFallbackSource(node: FlowNode, valid: Set<string>, current: string | null): string | null {
	if (valid.has('output')) return 'output';
	if (current && valid.has(current)) return current;
	if (valid.has('true')) return 'true';
	if (valid.has('default')) return 'default';
	const first = valid.values().next().value;
	return first ?? null;
}

function pickFallbackTarget(node: FlowNode, valid: Set<string>, current: string | null): string | null {
	if (current && valid.has(current)) return current;
	if (valid.has('input')) return 'input';
	const first = valid.values().next().value;
	return first ?? null;
}

function pickLogicGateTarget(
	node: FlowNode,
	valid: Set<string>,
	current: string | null,
	usedByNode: Map<string, Set<string>>,
): string | null {
	const nodeId = String(node.id);
	if (!usedByNode.has(nodeId)) usedByNode.set(nodeId, new Set());
	const used = usedByNode.get(nodeId)!;

	if (current && valid.has(current) && !used.has(current)) {
		used.add(current);
		return current;
	}

	const sorted = [...valid].sort();
	for (const handle of sorted) {
		if (!used.has(handle)) {
			used.add(handle);
			return handle;
		}
	}
	return null;
}

function repairEdgeHandles(
	nodes: FlowNode[],
	edges: FlowEdge[],
): { edges: FlowEdge[]; handlesRepaired: number; invalidHandleEdgesDropped: number } {
	const byId = new Map(nodes.map((n) => [String(n.id), n]));
	const usedLogicGateTargets = new Map<string, Set<string>>();
	let handlesRepaired = 0;
	let invalidHandleEdgesDropped = 0;
	const out: FlowEdge[] = [];

	for (const e of edges) {
		const src = byId.get(String(e.source));
		const tgt = byId.get(String(e.target));
		if (!src || !tgt) continue;

		const srcValid = validSourceHandleIds(src);
		const tgtValid = validTargetHandleIds(tgt);

		let sh = e.sourceHandle != null && e.sourceHandle !== '' ? String(e.sourceHandle) : null;
		let th = e.targetHandle != null && e.targetHandle !== '' ? String(e.targetHandle) : null;

		if (!sh || !srcValid.has(sh)) {
			const next = pickFallbackSource(src, srcValid, sh);
			if (!next) {
				invalidHandleEdgesDropped++;
				continue;
			}
			if (sh !== next) handlesRepaired++;
			sh = next;
		}

		const tgtType = String(tgt.type || '');
		if (
			!th ||
			!tgtValid.has(th) ||
			(tgtType === 'logicGate' && usedLogicGateTargets.get(String(tgt.id))?.has(th))
		) {
			const next =
				tgtType === 'logicGate'
					? pickLogicGateTarget(tgt, tgtValid, th, usedLogicGateTargets)
					: pickFallbackTarget(tgt, tgtValid, th);
			if (!next) {
				invalidHandleEdgesDropped++;
				continue;
			}
			if (th !== next) handlesRepaired++;
			th = next;
		} else if (tgtType === 'logicGate') {
			pickLogicGateTarget(tgt, tgtValid, th, usedLogicGateTargets);
		}

		out.push({ ...e, sourceHandle: sh, targetHandle: th });
	}

	return { edges: out, handlesRepaired, invalidHandleEdgesDropped };
}

let fallbackCounter = 0;

function newEdgeId(): string {
	if (typeof crypto !== 'undefined' && crypto.randomUUID) {
		return `e-${crypto.randomUUID()}`;
	}
	++fallbackCounter;
	return `e-${Date.now()}-${fallbackCounter}-${Math.random().toString(36).slice(2, 9)}`;
}

export interface SanitizeFlowGraphResult {
	nodes: FlowNode[];
	edges: FlowEdge[];
	duplicateNodesDropped: number;
	duplicateEdgeIdsRewritten: number;
	danglingEdgesRemoved: number;
	edgeHandlesRepaired: number;
	edgesDroppedInvalidHandles: number;
}

export function sanitizeFlowNodesAndEdges(
	nodes: FlowNode[],
	edges: FlowEdge[],
	opts?: { logWarnings?: boolean },
): SanitizeFlowGraphResult {
	const seenNodeIds = new Set<string>();
	const uniqueNodes: FlowNode[] = [];
	let duplicateNodesDropped = 0;

	for (const n of nodes) {
		const id = String(n?.id ?? '');
		if (!id || seenNodeIds.has(id)) {
			duplicateNodesDropped++;
			continue;
		}
		seenNodeIds.add(id);
		uniqueNodes.push(n);
	}

	const beforeEdgeCount = edges.length;
	const noteIds = new Set(
		uniqueNodes.filter((n) => String(n.type) === 'note').map((n) => String(n.id)),
	);
	const connectedEdges = edges.filter((e) => {
		const s = String(e?.source ?? '');
		const t = String(e?.target ?? '');
		if (!s || !t || s === t || !seenNodeIds.has(s) || !seenNodeIds.has(t)) return false;
		if (noteIds.has(s) || noteIds.has(t)) return false;
		return true;
	});
	const danglingEdgesRemoved = beforeEdgeCount - connectedEdges.length;

	const seenEdgeIds = new Set<string>();
	const uniqueEdges: FlowEdge[] = [];
	let duplicateEdgeIdsRewritten = 0;

	for (const e of connectedEdges) {
		let id = String(e?.id ?? '');
		if (!id || seenEdgeIds.has(id)) {
			duplicateEdgeIdsRewritten++;
			let nextId = newEdgeId();
			while (seenEdgeIds.has(nextId)) nextId = newEdgeId();
			id = nextId;
		}
		seenEdgeIds.add(id);
		uniqueEdges.push({ ...e, id });
	}

	const repaired = repairEdgeHandles(uniqueNodes, uniqueEdges);
	const edgeHandlesRepaired = repaired.handlesRepaired;
	const edgesDroppedInvalidHandles = repaired.invalidHandleEdgesDropped;

	if (opts?.logWarnings !== false && typeof console !== 'undefined') {
		if (duplicateNodesDropped > 0) {
			console.warn(
				`[flow] Removed ${duplicateNodesDropped} duplicate or empty-id node(s) to avoid render errors.`,
			);
		}
		if (duplicateEdgeIdsRewritten > 0) {
			console.warn(
				`[flow] Rewrote ${duplicateEdgeIdsRewritten} duplicate edge id(s) to keep the graph valid.`,
			);
		}
		if (danglingEdgesRemoved > 0) {
			console.warn(`[flow] Removed ${danglingEdgesRemoved} edge(s) with missing endpoints.`);
		}
		if (edgeHandlesRepaired > 0) {
			console.warn(
				`[flow] Repaired ${edgeHandlesRepaired} edge handle reference(s) for XYFlow compatibility.`,
			);
		}
		if (edgesDroppedInvalidHandles > 0) {
			console.warn(
				`[flow] Dropped ${edgesDroppedInvalidHandles} edge(s) that could not be mapped to valid handles.`,
			);
		}
	}

	return {
		nodes: uniqueNodes,
		edges: repaired.edges,
		duplicateNodesDropped,
		duplicateEdgeIdsRewritten,
		danglingEdgesRemoved,
		edgeHandlesRepaired,
		edgesDroppedInvalidHandles,
	};
}
